import {Inject, Injectable, Type} from '@angular/core';
import {IdName} from '@domain/id-name';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {
  AssertionUtils,
  BaseComponent,
  ButtonAction,
  CrudOverviewButtonConfig,
  CrudOverviewButtonConfigs,
  DialogBuilderFactoryService,
  DialogType,
  GridButtonClickedEvent,
  NavigationHelperPrototypeService,
  TranslateService
} from '@vdw/angular-component-library';
import {cloneDeep} from 'lodash-es';
import {Subject, takeUntil} from 'rxjs';
import {PageTypeEnum} from '../navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '../remove-confirmation/remove-confirmation.service';
import {AddComponentInput} from '../routing/add-component-input.interface';
import {CrudActions} from './crud-actions.interface';

@Injectable({providedIn: 'root'})
export class GridActionButtonsHelper extends BaseComponent {
  private readonly deleteButton: CrudOverviewButtonConfig = {
    action: ButtonAction.DELETE,
    title: 'ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.DELETE',
    icon: 'solid-delete',
    withTooltip: true,
    onlyEnabledOnSingleRowSelection: true
  };

  private readonly editButton: CrudOverviewButtonConfig = {
    action: ButtonAction.EDIT,
    title: 'ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.EDIT',
    icon: 'solid-edit',
    onlyEnabledOnSingleRowSelection: true,
    withTooltip: true
  };

  private readonly duplicateButton: CrudOverviewButtonConfig = {
    action: ButtonAction.DUPLICATE,
    title: 'ANGULAR_COMPONENT_LIBRARY.CRUD_OVERVIEW_DATA.DUPLICATE',
    icon: 'solid-file-copy',
    onlyEnabledOnSingleRowSelection: true,
    withTooltip: true
  };

  private currentSubscription: ProfileSubscription;

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly translate: TranslateService,
    private readonly removeConfirmation: RemoveConfirmationService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();
  }

  public getActionButtonsForGridSelection(crudActions: CrudActions): CrudOverviewButtonConfig[] {
    let actionButtons: CrudOverviewButtonConfig[] = [];
    if (this.hasPermissionForAction(crudActions?.requiredPermissionToEdit)) {
      if (!AssertionUtils.isNullOrUndefined(crudActions?.withEdit)) {
        this.editButton.onClick = crudActions.withEdit;
        actionButtons.push(this.editButton);
      }

      if (!AssertionUtils.isNullOrUndefined(crudActions?.withDuplicate)) {
        this.duplicateButton.onClick = crudActions.withDuplicate;
        actionButtons.push(this.duplicateButton);
      }

      if (!AssertionUtils.isNullOrUndefined(crudActions?.withDelete)) {
        this.deleteButton.onClick = crudActions.withDelete;
        if (crudActions.allowDeleteMultiple) {
          this.deleteButton.onlyEnabledOnSingleRowSelection = false;
        }
        this.deleteButton.canShowButton = crudActions.canBeDeleted;
        actionButtons.push(this.deleteButton);
      }

      return cloneDeep(actionButtons);
    } else {
      return actionButtons;
    }
  }

  public getActionButtonsWithRouting(
    addComponent: Type<any>,
    editPermission: Permission,
    withDelete: (event: GridButtonClickedEvent) => void,
    nextComponentModule?: Type<any>
  ): CrudOverviewButtonConfigs {
    const rowActionsComplete = new Subject();

    return {
      crudOverviewButtonConfigs: this.getActionButtonsForGridSelection({
        withDelete: withDelete,
        requiredPermissionToEdit: editPermission,
        withEdit: ({rowNode}: GridButtonClickedEvent) =>
          this.navigationHelper
            .navigateWithContentSwitcher({
              componentInfo: {component: addComponent, module: nextComponentModule, componentInputs: {pageType: PageTypeEnum.EDIT, itemId: rowNode.data.id, canShowHeader: true} as AddComponentInput}
            })
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe((data: any) => rowActionsComplete.next(data)),
        withDuplicate: ({rowNode}: GridButtonClickedEvent) =>
          this.navigationHelper
            .navigateWithContentSwitcher({
              componentInfo: {
                component: addComponent,
                module: nextComponentModule,
                componentInputs: {pageType: PageTypeEnum.DUPLICATE, itemId: rowNode.data.id, canShowHeader: true} as AddComponentInput
              }
            })
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe((data: any) => rowActionsComplete.next(data))
      }),
      rowActionsComplete: rowActionsComplete
    } as CrudOverviewButtonConfigs;
  }

  public getCrudOverviewButtonConfigs(getSelectedObjects: () => IdName[], getDeleteService: (id: number) => any, translationKey: string): (event: GridButtonClickedEvent) => void {
    return ({gridApi, rowNode}: GridButtonClickedEvent) => {
      const data = AssertionUtils.isNullOrUndefined(rowNode) ? (gridApi?.getSelectedRows()[0] as IdName) : (rowNode.data as IdName);
      const selectedObjects = getSelectedObjects();

      if (!AssertionUtils.isEmpty(selectedObjects) && data.id === selectedObjects[0]?.id) {
        this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
          titleText: this.translate.instant('CONFLICTS.DIALOG.IN_USE', {object: data.name}),
          messageText: this.translate.instant('CONFLICTS.DIALOG.DESELECT'),
          type: DialogType.DELETE
        });
        return;
      }

      this.removeConfirmation.open(data, getDeleteService(data.id), translationKey, gridApi);
    };
  }

  private hasPermissionForAction(permission: Permission): boolean {
    this.currentSubscription = this.authentication.getCurrentSubscription();

    return this.currentSubscription?.hasPermission(permission);
  }
}
